// Medical Scores Database - Extended Gastroenterology and Hepatology Scores
// Additional GI tract and liver assessment tools

import {
  Beaker,
  Activity,
  AlertTriangle,
  Droplets
} from 'lucide-react';

// ==================== HÉPATO-GASTRO ÉTENDU ====================
export const GASTRO_EXTENDED_SCORES = {
  balthazar: {
    name: 'Score de Balthazar',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Gravité pancréatite aiguë au scanner',
    color: 'amber',
    fields: [
      { id: 'grade', label: 'Grade scanner', type: 'select', options: [
        { value: 0, label: 'A - Pancréas normal (0 pt)' },
        { value: 1, label: 'B - Élargissement focal ou diffus (1 pt)' },
        { value: 2, label: 'C - Inflammation péripancréatique (2 pts)' },
        { value: 3, label: 'D - Collection liquidienne unique (3 pts)' },
        { value: 4, label: 'E - ≥ 2 collections ou gaz (4 pts)' }
      ]},
      { id: 'necrosis', label: 'Nécrose pancréatique', type: 'select', options: [
        { value: 0, label: 'Pas de nécrose (0 pt)' },
        { value: 2, label: '< 30% (2 pts)' },
        { value: 4, label: '30-50% (4 pts)' },
        { value: 6, label: '> 50% (6 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.grade || 0) + (values.necrosis || 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `Balthazar ${score}`, detail: 'Morbidité 8%, mortalité 3%' };
      if (score <= 6) return { level: 'warning', text: `Balthazar ${score}`, detail: 'Morbidité 35%, mortalité 6%' };
      return { level: 'danger', text: `Balthazar ${score}`, detail: 'Morbidité 92%, mortalité 17%' };
    },
    formula: 'Balthazar (CTSI) = Grade A-E + Nécrose'
  },

  bisap: {
    name: 'Score BISAP',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Pronostic pancréatite aiguë (simple)',
    color: 'amber',
    fields: [
      { id: 'bun', label: 'Urée > 25 mg/dL (8.9 mmol/L)', type: 'checkbox', points: 1 },
      { id: 'impaired', label: 'Altération conscience', type: 'checkbox', points: 1 },
      { id: 'sirs', label: 'SIRS (≥ 2 critères)', type: 'checkbox', points: 1 },
      { id: 'age', label: 'Âge > 60 ans', type: 'checkbox', points: 1 },
      { id: 'pleural', label: 'Épanchement pleural au scanner', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      const mortalities = ['< 1%', '< 1%', '2%', '5-8%', '11-22%', '> 22%'];
      if (score <= 1) return { level: 'success', text: `BISAP ${score}`, detail: `Mortalité: ${mortalities[score]}` };
      if (score <= 2) return { level: 'warning', text: `BISAP ${score}`, detail: `Mortalité: ${mortalities[score]}` };
      return { level: 'danger', text: `BISAP ${score}`, detail: `Mortalité: ${mortalities[score]}` };
    },
    formula: 'BISAP = BUN + Impaired mental + SIRS + Age > 60 + Pleural effusion'
  },

  haps: {
    name: 'Harmless Acute Pancreatitis Score',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Identification pancréatite légère',
    color: 'amber',
    fields: [
      { id: 'rebound', label: 'Défense abdominale présente', type: 'checkbox', points: 1 },
      { id: 'creatinine', label: 'Créatinine > 177 µmol/L (2 mg/dL)', type: 'checkbox', points: 1 },
      { id: 'hematocrit', label: 'Hématocrite > 43% (H) ou > 39.6% (F)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 3 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'HAPS négatif', detail: 'Pancréatite bénigne très probable. Sortie possible' };
      return { level: 'warning', text: `HAPS ${score}/3`, detail: 'Pancréatite potentiellement sévère. Surveillance' };
    },
    formula: 'HAPS = 0 si 3 critères absents → pancréatite légère'
  },

  forrest: {
    name: 'Classification de Forrest',
    category: 'Hépato-Gastro',
    icon: Droplets,
    description: 'Risque récidive hémorragie ulcéreuse',
    color: 'amber',
    fields: [
      { id: 'class', label: 'Classification endoscopique', type: 'select', options: [
        { value: 'Ia', label: 'Ia - Hémorragie en jet (Spurting)' },
        { value: 'Ib', label: 'Ib - Hémorragie en nappe (Oozing)' },
        { value: 'IIa', label: 'IIa - Vaisseau visible non hémorragique' },
        { value: 'IIb', label: 'IIb - Caillot adhérent' },
        { value: 'IIc', label: 'IIc - Taches pigmentées' },
        { value: 'III', label: 'III - Fond propre' }
      ]}
    ],
    calculate: (values) => {
      const riskMap = { 'Ia': 90, 'Ib': 55, 'IIa': 43, 'IIb': 22, 'IIc': 7, 'III': 2 };
      const risk = riskMap[values.class] || 0;
      return { score: values.class || 'III', max: '', details: { risk } };
    },
    interpret: (score, result) => {
      const risk = result?.details?.risk || 0;
      if (risk >= 50) return { level: 'danger', text: `Forrest ${score}`, detail: `Récidive: ${risk}%. Traitement endoscopique urgent` };
      if (risk >= 20) return { level: 'warning', text: `Forrest ${score}`, detail: `Récidive: ${risk}%. Traitement endoscopique recommandé` };
      if (risk >= 5) return { level: 'warning', text: `Forrest ${score}`, detail: `Récidive: ${risk}%. Surveillance, IPP haute dose` };
      return { level: 'success', text: `Forrest ${score}`, detail: `Récidive: ${risk}%. Risque faible` };
    },
    formula: 'Forrest I = hémorragie active, II = stigmates, III = propre'
  },

  rockall: {
    name: 'Score de Rockall',
    category: 'Hépato-Gastro',
    icon: Droplets,
    description: 'Risque récidive et mortalité hémorragie digestive',
    color: 'amber',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 60 ans (0 pt)' },
        { value: 1, label: '60-79 ans (1 pt)' },
        { value: 2, label: '≥ 80 ans (2 pts)' }
      ]},
      { id: 'shock', label: 'État hémodynamique', type: 'select', options: [
        { value: 0, label: 'Stable (0 pt)' },
        { value: 1, label: 'Tachycardie > 100/min (1 pt)' },
        { value: 2, label: 'Hypotension PAS < 100 mmHg (2 pts)' }
      ]},
      { id: 'comorbidity', label: 'Comorbidités', type: 'select', options: [
        { value: 0, label: 'Aucune majeure (0 pt)' },
        { value: 2, label: 'IC, coronaropathie, autres (2 pts)' },
        { value: 3, label: 'Insuffisance rénale, hépatique, cancer disséminé (3 pts)' }
      ]},
      { id: 'diagnosis', label: 'Diagnostic endoscopique', type: 'select', options: [
        { value: 0, label: 'Mallory-Weiss, pas de lésion (0 pt)' },
        { value: 1, label: 'Autre diagnostic (1 pt)' },
        { value: 2, label: 'Cancer digestif (2 pts)' }
      ]},
      { id: 'stigmata', label: 'Stigmates hémorragiques', type: 'select', options: [
        { value: 0, label: 'Aucun ou tache noire (0 pt)' },
        { value: 2, label: 'Sang, vaisseau visible, caillot (2 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 11 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Rockall ${score}`, detail: 'Faible risque. Retour à domicile envisageable' };
      if (score <= 4) return { level: 'warning', text: `Rockall ${score}`, detail: 'Risque modéré. Hospitalisation courte' };
      if (score <= 7) return { level: 'danger', text: `Rockall ${score}`, detail: 'Risque élevé. Surveillance USI' };
      return { level: 'danger', text: `Rockall ${score}`, detail: 'Risque très élevé. Mortalité > 40%' };
    },
    formula: 'Rockall = Âge + Choc + Comorbidités + Diagnostic + Stigmates'
  },

  maddrey: {
    name: 'Score de Maddrey',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Pronostic hépatite alcoolique aiguë',
    color: 'amber',
    fields: [
      { id: 'pt', label: 'TP patient (secondes)', type: 'number', min: 10, max: 100 },
      { id: 'ptControl', label: 'TP témoin (secondes)', type: 'number', min: 10, max: 20 },
      { id: 'bilirubin', label: 'Bilirubine totale (µmol/L)', type: 'number', min: 10, max: 1000 }
    ],
    calculate: (values) => {
      const pt = values.pt || 15;
      const ptControl = values.ptControl || 12;
      const bili = values.bilirubin || 100;
      // Maddrey = 4.6 × (TP patient - TP témoin) + Bili (mg/dL)
      // Bili en mg/dL = µmol/L / 17.1
      const biliMgdL = bili / 17.1;
      const mdf = 4.6 * (pt - ptControl) + biliMgdL;
      return { score: Math.round(mdf), max: '' };
    },
    interpret: (score) => {
      if (score < 32) return { level: 'success', text: `Maddrey ${score}`, detail: 'Hépatite alcoolique modérée. Survie 90%' };
      return { level: 'danger', text: `Maddrey ${score}`, detail: 'Hépatite alcoolique sévère. Corticothérapie indiquée si pas de CI' };
    },
    formula: 'Maddrey = 4.6 × (TP patient - TP témoin) + Bilirubine (mg/dL)'
  },

  lille: {
    name: 'Score de Lille',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Réponse corticothérapie hépatite alcoolique',
    color: 'amber',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'albumin', label: 'Albumine J0 (g/L)', type: 'number', min: 10, max: 50 },
      { id: 'biliJ0', label: 'Bilirubine J0 (µmol/L)', type: 'number', min: 10, max: 1000 },
      { id: 'biliJ7', label: 'Bilirubine J7 (µmol/L)', type: 'number', min: 10, max: 1000 },
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 30, max: 500 },
      { id: 'pt', label: 'TP (%)', type: 'number', min: 10, max: 100 }
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const alb = values.albumin || 28;
      const biliJ0 = values.biliJ0 || 300;
      const biliJ7 = values.biliJ7 || 200;
      const creat = values.creatinine || 80;
      const pt = values.pt || 40;
      
      // Formule simplifiée de Lille
      const evolution = biliJ0 - biliJ7;
      const creatBin = creat > 115 ? 1 : 0;
      
      const R = 3.19 - 0.101 * age + 0.147 * alb + 0.0165 * evolution - 0.206 * creatBin - 0.0206 * biliJ0 + 0.0011 * biliJ0 * (biliJ7 > biliJ0 ? 1 : 0);
      const lille = Math.exp(-R) / (1 + Math.exp(-R));
      
      return { score: lille.toFixed(3), max: 1 };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 0.45) return { level: 'success', text: `Lille ${score}`, detail: 'Répondeur. Poursuivre corticothérapie 28 jours' };
      if (s < 0.56) return { level: 'warning', text: `Lille ${score}`, detail: 'Réponse partielle. Discuter poursuite' };
      return { level: 'danger', text: `Lille ${score}`, detail: 'Non répondeur. Arrêt corticoïdes. Survie 25% à 6 mois' };
    },
    formula: 'Lille calculé à J7 de corticothérapie. < 0.45 = répondeur'
  },

  harveyBradshaw: {
    name: 'Indice de Harvey-Bradshaw',
    category: 'Hépato-Gastro',
    icon: Activity,
    description: 'Activité maladie de Crohn (simplifié)',
    color: 'amber',
    fields: [
      { id: 'wellbeing', label: 'Bien-être général', type: 'select', options: [
        { value: 0, label: '0 - Très bien' }, { value: 1, label: '1 - Légèrement inférieur' },
        { value: 2, label: '2 - Médiocre' }, { value: 3, label: '3 - Mauvais' }, { value: 4, label: '4 - Très mauvais' }
      ]},
      { id: 'abdominalPain', label: 'Douleur abdominale', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'stools', label: 'Nombre de selles liquides/jour', type: 'number', min: 0, max: 20 },
      { id: 'mass', label: 'Masse abdominale', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Douteuse' },
        { value: 2, label: '2 - Certaine' }, { value: 3, label: '3 - Certaine et douloureuse' }
      ]},
      { id: 'complications', label: 'Complications extra-digestives (1 pt chacune)', type: 'number', min: 0, max: 10 }
    ],
    calculate: (values) => {
      const total = (values.wellbeing || 0) + (values.abdominalPain || 0) + 
                   (values.stools || 0) + (values.mass || 0) + (values.complications || 0);
      return { score: total, max: '' };
    },
    interpret: (score) => {
      if (score < 5) return { level: 'success', text: `HBI ${score}`, detail: 'Rémission' };
      if (score < 8) return { level: 'warning', text: `HBI ${score}`, detail: 'Activité légère' };
      if (score < 16) return { level: 'warning', text: `HBI ${score}`, detail: 'Activité modérée' };
      return { level: 'danger', text: `HBI ${score}`, detail: 'Activité sévère' };
    },
    formula: 'HBI = Bien-être + Douleur + Selles + Masse + Complications'
  },

  lichtiger: {
    name: 'Score de Lichtiger',
    category: 'Hépato-Gastro',
    icon: Activity,
    description: 'Suivi colite aiguë grave',
    color: 'amber',
    fields: [
      { id: 'diarrhea', label: 'Nombre de selles/jour', type: 'select', options: [
        { value: 0, label: '0 - 0-2' }, { value: 1, label: '1 - 3-4' },
        { value: 2, label: '2 - 5-6' }, { value: 3, label: '3 - 7-9' }, { value: 4, label: '4 - ≥ 10' }
      ]},
      { id: 'nightDiarrhea', label: 'Diarrhée nocturne', type: 'select', options: [
        { value: 0, label: '0 - Non' }, { value: 1, label: '1 - Oui' }
      ]},
      { id: 'blood', label: 'Sang visible dans les selles', type: 'select', options: [
        { value: 0, label: '0 - Traces' }, { value: 1, label: '1 - Stries' },
        { value: 2, label: '2 - Sang évident' }, { value: 3, label: '3 - Sang prédominant' }
      ]},
      { id: 'incontinence', label: 'Incontinence fécale', type: 'select', options: [
        { value: 0, label: '0 - Non' }, { value: 1, label: '1 - Oui' }
      ]},
      { id: 'pain', label: 'Douleur abdominale', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'wellbeing', label: 'État général', type: 'select', options: [
        { value: 0, label: '0 - Parfait' }, { value: 1, label: '1 - Très bon' },
        { value: 2, label: '2 - Bon' }, { value: 3, label: '3 - Moyen' },
        { value: 4, label: '4 - Médiocre' }, { value: 5, label: '5 - Très mauvais' }
      ]},
      { id: 'tenderness', label: 'Sensibilité abdominale', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Rebond' }
      ]},
      { id: 'antidiarrheals', label: 'Besoin antidiarrhéiques', type: 'select', options: [
        { value: 0, label: '0 - Non' }, { value: 1, label: '1 - Oui' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 21 };
    },
    interpret: (score) => {
      if (score < 10) return { level: 'success', text: `Lichtiger ${score}/21`, detail: 'Réponse au traitement. Amélioration' };
      return { level: 'danger', text: `Lichtiger ${score}/21`, detail: 'Colite sévère. Discuter colectomie si pas d\'amélioration' };
    },
    formula: 'Lichtiger < 10 après 3-5j IV = réponse'
  },

  trueloveWitts: {
    name: 'Critères de Truelove et Witts',
    category: 'Hépato-Gastro',
    icon: AlertTriangle,
    description: 'Sévérité rectocolite hémorragique',
    color: 'amber',
    fields: [
      { id: 'stools', label: 'Selles sanglantes/jour', type: 'select', options: [
        { value: 'mild', label: 'Légère: < 4' },
        { value: 'moderate', label: 'Modérée: 4-6' },
        { value: 'severe', label: 'Sévère: > 6' }
      ]},
      { id: 'pulse', label: 'Pouls', type: 'select', options: [
        { value: 'mild', label: 'Légère: < 90/min' },
        { value: 'moderate', label: 'Modérée: 90-100/min' },
        { value: 'severe', label: 'Sévère: > 90/min' }
      ]},
      { id: 'temp', label: 'Température', type: 'select', options: [
        { value: 'mild', label: 'Légère: < 37.5°C' },
        { value: 'moderate', label: 'Modérée: 37.5-37.8°C' },
        { value: 'severe', label: 'Sévère: > 37.8°C' }
      ]},
      { id: 'hb', label: 'Hémoglobine', type: 'select', options: [
        { value: 'mild', label: 'Légère: > 11 g/dL' },
        { value: 'moderate', label: 'Modérée: 10.5-11 g/dL' },
        { value: 'severe', label: 'Sévère: < 10.5 g/dL' }
      ]},
      { id: 'esr', label: 'VS', type: 'select', options: [
        { value: 'mild', label: 'Légère: < 20 mm/h' },
        { value: 'moderate', label: 'Modérée: 20-30 mm/h' },
        { value: 'severe', label: 'Sévère: > 30 mm/h' }
      ]}
    ],
    calculate: (values) => {
      let severeCount = 0;
      Object.values(values).forEach(v => { if (v === 'severe') severeCount++; });
      return { score: severeCount, max: 5 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Poussée légère', detail: 'Traitement ambulatoire' };
      if (score <= 2) return { level: 'warning', text: 'Poussée modérée', detail: 'Corticoïdes per os' };
      return { level: 'danger', text: 'Poussée sévère', detail: 'Hospitalisation, corticoïdes IV, discuter colectomie' };
    },
    formula: 'Truelove-Witts: sévère si selles > 6/j + 1 critère systémique'
  },

  apri: {
    name: 'Score APRI',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Probabilité fibrose hépatique',
    color: 'amber',
    fields: [
      { id: 'ast', label: 'ASAT (UI/L)', type: 'number', min: 5, max: 1000 },
      { id: 'astUpperLimit', label: 'Limite sup normale ASAT (UI/L)', type: 'number', min: 20, max: 60 },
      { id: 'platelets', label: 'Plaquettes (×10⁹/L)', type: 'number', min: 10, max: 500 }
    ],
    calculate: (values) => {
      const ast = values.ast || 50;
      const ul = values.astUpperLimit || 40;
      const plt = values.platelets || 200;
      const apri = ((ast / ul) / plt) * 100;
      return { score: apri.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 0.5) return { level: 'success', text: `APRI ${score}`, detail: 'Fibrose significative peu probable' };
      if (s < 1.5) return { level: 'warning', text: `APRI ${score}`, detail: 'Zone grise - biopsie à discuter' };
      return { level: 'danger', text: `APRI ${score}`, detail: 'Fibrose significative probable' };
    },
    formula: 'APRI = [(ASAT/limite sup) / Plaquettes] × 100'
  },

  fib4: {
    name: 'Score FIB-4',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Identification fibrose hépatique',
    color: 'amber',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'ast', label: 'ASAT (UI/L)', type: 'number', min: 5, max: 1000 },
      { id: 'alt', label: 'ALAT (UI/L)', type: 'number', min: 5, max: 1000 },
      { id: 'platelets', label: 'Plaquettes (×10⁹/L)', type: 'number', min: 10, max: 500 }
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const ast = values.ast || 50;
      const alt = values.alt || 50;
      const plt = values.platelets || 200;
      const fib4 = (age * ast) / (plt * Math.sqrt(alt));
      return { score: fib4.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 1.3) return { level: 'success', text: `FIB-4 ${score}`, detail: 'Fibrose avancée peu probable (VPN 90%)' };
      if (s < 2.67) return { level: 'warning', text: `FIB-4 ${score}`, detail: 'Zone indéterminée - examens complémentaires' };
      return { level: 'danger', text: `FIB-4 ${score}`, detail: 'Fibrose avancée probable (VPP 80%)' };
    },
    formula: 'FIB-4 = (Âge × ASAT) / (Plaquettes × √ALAT)'
  },

  nafld: {
    name: 'NAFLD Fibrosis Score',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Risque fibrose sur stéatose non alcoolique',
    color: 'amber',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'bmi', label: 'IMC (kg/m²)', type: 'number', min: 15, max: 60, step: 0.1 },
      { id: 'diabetes', label: 'Diabète ou glycémie à jeun > 1.1 g/L', type: 'checkbox', points: 1 },
      { id: 'ast', label: 'ASAT (UI/L)', type: 'number', min: 5, max: 500 },
      { id: 'alt', label: 'ALAT (UI/L)', type: 'number', min: 5, max: 500 },
      { id: 'platelets', label: 'Plaquettes (×10⁹/L)', type: 'number', min: 10, max: 500 },
      { id: 'albumin', label: 'Albumine (g/L)', type: 'number', min: 15, max: 55 }
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const bmi = values.bmi || 28;
      const ifg = values.diabetes ? 1 : 0;
      const ratio = (values.ast || 30) / (values.alt || 40);
      const plt = values.platelets || 200;
      const alb = values.albumin || 40;
      
      const nfs = -1.675 + 0.037 * age + 0.094 * bmi + 1.13 * ifg + 0.99 * ratio - 0.013 * plt - 0.66 * alb;
      return { score: nfs.toFixed(3), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < -1.455) return { level: 'success', text: `NFS ${score}`, detail: 'Fibrose avancée peu probable (F0-F2)' };
      if (s < 0.676) return { level: 'warning', text: `NFS ${score}`, detail: 'Zone indéterminée - Fibroscan recommandé' };
      return { level: 'danger', text: `NFS ${score}`, detail: 'Fibrose avancée probable (F3-F4)' };
    },
    formula: 'NAFLD Score = -1.675 + 0.037×Âge + 0.094×IMC + 1.13×IFG + 0.99×AST/ALT - 0.013×PLT - 0.66×Alb'
  }
};

export default GASTRO_EXTENDED_SCORES;
